import React, { useEffect, useState } from 'react';
import { Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Select, InputLabel, FormControl } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Box } from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL;

const PatientAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [appointmentTime, setAppointmentTime] = useState(new Date());
  const [selectedTests, setSelectedTests] = useState([]);
  const [notes, setNotes] = useState('');
  const [tests, setTests] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchAppointments = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(`${API_URL}/api/patients/me/appointments`);
        setAppointments(res.data);
      } catch (err) {
        setError('Failed to load appointments');
      } finally {
        setLoading(false);
      }
    };
    const fetchTests = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/tests`);
        setTests(res.data);
      } catch {}
    };
    fetchAppointments();
    fetchTests();
  }, []);

  const handleSchedule = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/patients/me/appointments`, {
        appointmentTime,
        tests: selectedTests.map(id => ({ id })),
        notes
      });
      setShowDialog(false);
      setAppointmentTime(new Date());
      setSelectedTests([]);
      setNotes('');
      // Refresh appointments
      const res = await axios.get(`${API_URL}/api/patients/me/appointments`);
      setAppointments(res.data);
    } catch (err) {
      alert('Failed to schedule appointment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>My Appointments</Typography>
      <Button variant="contained" sx={{ mb: 2 }} onClick={() => setShowDialog(true)}>
        Schedule Appointment
      </Button>
      {loading ? <CircularProgress /> : error ? <Alert severity="error">{error}</Alert> : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date & Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Tests</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appointments.map(app => (
                <TableRow key={app.id}>
                  <TableCell>{new Date(app.appointmentTime).toLocaleString()}</TableCell>
                  <TableCell>{app.status}</TableCell>
                  <TableCell>{app.tests.map(t => t.name).join(', ')}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => navigate(`/patient-appointments/${app.id}`)}>View Details</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Appointment</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSchedule} sx={{ mt: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Appointment Date & Time"
                value={appointmentTime}
                onChange={setAppointmentTime}
                renderInput={(params) => <TextField {...params} fullWidth sx={{ mb: 2 }} />}
              />
            </LocalizationProvider>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Tests</InputLabel>
              <Select
                multiple
                value={selectedTests}
                onChange={e => setSelectedTests(e.target.value)}
                label="Tests"
                renderValue={selected => tests.filter(t => selected.includes(t.id)).map(t => t.name).join(', ')}
              >
                {tests.map(test => (
                  <MenuItem key={test.id} value={test.id}>{test.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Notes"
              fullWidth
              multiline
              minRows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              sx={{ mb: 2 }}
            />
            <DialogActions>
              <Button onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? 'Scheduling...' : 'Schedule'}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default PatientAppointments; 